import { useEffect, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGameStore, isAIGameMode, isHotseatMode, type GameMode } from '../../store/game-store';
import { useAuthStore } from '../../store/auth-store';
import { Color, GameStatus, GameVariant, resolveTimeControl, getVariantString } from '@draughts/shared';
import type { Move } from '@draughts/shared';
import type { NetworkClockState } from '../../types/clock';
import { List } from 'lucide-react';
import { trackGameCompleted } from '../../utils/analytics';
import GameScreen, { type MobilePanel } from './GameScreen';
import GameOverModal from './GameOverModal';
import DrawnOpeningBanner from '../online/DrawnOpeningBanner';
import LocalMoveList from './LocalMoveList';
import ReviewControls from './ReviewControls';
import { FlipBoardButton } from './FlipBoardButton';
import { AI_MOVE_DELAY_MS } from '../../constants/timing';
import { log } from '../../utils/logger';
import './LocalGame.css';

interface LocalGameProps {
  onLeave: () => void;
}

// Maps the AI game mode to its `game.modes.*` i18n label key so the opponent
// name can show the chosen difficulty (e.g. "Computer (Medium)").
const AI_MODE_LABEL_KEY: Partial<Record<GameMode, string>> = {
  'ai-very-easy': 'aiVeryEasy',
  'ai-easy': 'aiEasy',
  'ai-medium': 'aiMedium',
  'ai-hard': 'aiHard',
  'ai-expert': 'aiExpert',
  'ai-master': 'aiMaster',
};

export default function LocalGame({ onLeave }: LocalGameProps) {
  const { t } = useTranslation();
  const currentUser = useAuthStore((state) => state.user);

  // Game store state
  const currentTurn = useGameStore((state) => state.currentTurn);
  const status = useGameStore((state) => state.status);
  const variant = useGameStore((state) => state.variant);
  const gameMode = useGameStore((state) => state.gameMode);
  const isAIThinking = useGameStore((state) => state.isAIThinking);
  const makeAIMove = useGameStore((state) => state.makeAIMove);
  const moveHistory = useGameStore((state) => state.moveHistory);
  const halfMoveClock = useGameStore((state) => state.halfMoveClock);
  // Il ply in cui e' nato il 2-dame-contro-1 frisone: serve alla riga del
  // conteggio, ed e' `null` in ogni altra variante. Selettore reattivo e non
  // `getState()`, che in queste prove e' un mock senza `.getState`.
  const frisianEndgamePly = useGameStore((state) => state.frisianEndgamePly);
  const resign = useGameStore((state) => state.resign);
  const offerDraw = useGameStore((state) => state.offerDraw);
  const agreeDraw = useGameStore((state) => state.agreeDraw);
  const abandonGame = useGameStore((state) => state.abandonGame);
  const drawOffered = useGameStore((state) => state.drawOffered);
  const aiDeclinedDraw = useGameStore((state) => state.aiDeclinedDraw);
  const resetGame = useGameStore((state) => state.resetGame);
  const reviewIndex = useGameStore((state) => state.reviewIndex);
  const localPlayerColor = useGameStore((state) => state.localPlayerColor);
  const startedAtMs = useGameStore((state) => state.startedAtMs);
  const ruleMode = useGameStore((state) => state.ruleMode);
  const drawnOpening = useGameStore((state) => state.drawnOpening);
  const openingPlyCount = useGameStore((state) => state.openingPlyCount);
  const matchLegs = useGameStore((state) => state.matchLegs);
  const matchLegIndex = useGameStore((state) => state.matchLegIndex);
  const startReturnLeg = useGameStore((state) => state.startReturnLeg);

  // Clock state
  const clockState = useGameStore((state) => state.clockState);
  const timeControlPreset = useGameStore((state) => state.timeControlPreset);
  const updateClock = useGameStore((state) => state.updateClock);
  const handleTimeExpired = useGameStore((state) => state.handleTimeExpired);
  const addIncrement = useGameStore((state) => state.addIncrement);

  const isGameOver = status !== GameStatus.PLAYING && status !== GameStatus.WAITING;
  const isAIMode = isAIGameMode(gameMode);
  const isHotseat = isHotseatMode(gameMode);
  const aiColor = localPlayerColor === Color.WHITE ? Color.BLACK : Color.WHITE;
  const isAITurn = isAIMode && currentTurn === aiColor;

  // `resolveTimeControl` e non l'indice su TIME_CONTROL_PRESETS: la cadenza ora puo'
  // essere una qualsiasi delle ~490 combinazioni della partita personalizzata, e la
  // tabella dei preset ne conosce 18 — tutte le altre uscivano come '—'.
  const timeControlLabel = timeControlPreset !== 'none'
    ? (resolveTimeControl(timeControlPreset)?.name ?? '—')
    // `game.timeControls.noTimeLimit`, la stessa chiave che usa OnlineGame per la stessa
    // cosa. Qui c'era `t('game.timeControl.unlimited', 'Unlimited')` — chiave che non
    // esiste in nessuno dei sette locale, e con il default inline a coprirla l'unico
    // effetto visibile era la parola «Unlimited» in mezzo all'intestazione italiana.
    : t('game.timeControls.noTimeLimit');

  // Log mount
  useEffect(() => {
    log.info('LocalGame', 'Mounted', { variant, gameMode, timeControl: timeControlPreset });
  }, []);

  // Track game completed
  useEffect(() => {
    /*
     * Una partita chiusa senza vincitore non entra nelle statistiche: non ha un
     * esito da registrare, e il calcolo qui sotto — che non ha un quarto valore
     * da dare — la conterebbe come una SCONFITTA.
     */
    if (isGameOver && status !== GameStatus.ABANDONED && (isAIMode || isHotseat)) {
      log.info('LocalGame', 'Game complete', { status, variant, moves: moveHistory.length });
      // In presenza non c'e' un "io": il risultato si registra dal punto di vista
      // del Bianco, cosi' i numeri restano confrontabili fra partite.
      const refColor = isAIMode ? localPlayerColor : Color.WHITE;
      const playerWinsStatus = refColor === Color.WHITE ? GameStatus.WHITE_WINS : GameStatus.BLACK_WINS;
      const result = status === playerWinsStatus ? 'win' : status === GameStatus.DRAW ? 'draw' : 'loss';
      trackGameCompleted({
        variant,
        timeControl: timeControlPreset,
        result,
        mode: isAIMode ? 'ai' : 'hotseat',
        moves: moveHistory.length,
        durationSeconds: startedAtMs ? Math.round((Date.now() - startedAtMs) / 1000) : 0,
        ruleMode,
      });
    }
  }, [isGameOver]);

  // Trigger AI move. Paused while reviewing (reviewIndex !== null): applyAIResult
  // drops any result that lands during review, so dispatching here would busy-loop
  // (dispatch → drop → dispatch). The AI resumes automatically on go-live.
  useEffect(() => {
    if (isAITurn && !isGameOver && !isAIThinking && reviewIndex === null) {
      const timeout = setTimeout(() => {
        makeAIMove();
      }, AI_MOVE_DELAY_MS);
      return () => clearTimeout(timeout);
    }
    return undefined;
  }, [isAITurn, isGameOver, isAIThinking, reviewIndex, makeAIMove]);

  // Warn user when closing tab during active game
  useEffect(() => {
    if (status !== GameStatus.PLAYING) return;
    log.info('LocalGame', 'Beforeunload handler registered');
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [status]);

  // Track previous turn to add increment when a move is made
  const prevTurnRef = useRef(currentTurn);
  const moveHistoryLength = moveHistory.length;
  // L'incremento si aggancia al NUMERO DI MOSSE, non al cambio di turno.
  // Anche un annulla mossa cambia il turno, e in presenza si annulla liberamente:
  // agganciato al turno, ogni passo indietro avrebbe regalato un incremento.
  const prevMovesRef = useRef(moveHistoryLength);

  useEffect(() => {
    const hasTC = timeControlPreset !== 'none';
    const grew = moveHistoryLength > prevMovesRef.current;
    prevMovesRef.current = moveHistoryLength;
    // Le semi-mosse dell'apertura sorteggiata sono mosse vere in moveHistory, ma
    // nessuno le ha giocate: non danno incremento.
    if (!hasTC || !grew || moveHistoryLength <= openingPlyCount) {
      prevTurnRef.current = currentTurn;
      return;
    }

    if (prevTurnRef.current !== currentTurn) {
      const playerWhoMoved = prevTurnRef.current;
      addIncrement(playerWhoMoved);
      prevTurnRef.current = currentTurn;
    }
  }, [currentTurn, moveHistoryLength, openingPlyCount, timeControlPreset, addIncrement]);

  // Clock tick callback for local game
  const hasTimeControl = timeControlPreset !== 'none';
  // L'orologio parte alla prima mossa GIOCATA. Con un'apertura sorteggiata
  // moveHistory e' gia' piena all'avvio, e `> 0` faceva partire gli orologi prima
  // che qualcuno avesse mosso.
  const isClockActive = hasTimeControl && status === GameStatus.PLAYING && moveHistoryLength > openingPlyCount;

  // Convert local ClockState to NetworkClockState format
  const networkClockState: NetworkClockState | null = hasTimeControl ? {
    white: clockState.white.timeRemaining,
    black: clockState.black.timeRemaining,
    activePlayer: isClockActive
      ? (currentTurn === Color.WHITE ? 'white' : 'black')
      : null,
  } : null;

  // Re-anchor the tick clock whenever it is not actively counting (game over,
  // before the first move, between games). This makes the first tick after the
  // clock resumes debit only real elapsed time, never the idle gap.
  const lastTickAtRef = useRef<number | null>(null);
  useEffect(() => {
    if (!isClockActive) {
      lastTickAtRef.current = null;
    }
  }, [isClockActive]);

  // Local clock tick: debit the active player by the REAL wall-clock time
  // elapsed since the previous tick, not a fixed step. useClockTick's interval
  // (LOCAL_TICK_INTERVAL) is only a display cadence, not a time unit — assuming
  // a fixed 100ms decrement made the clock run at ~0.4x real speed once the
  // interval moved to 250ms. Mirrors the online store's tickClock, which
  // decrements by `Date.now() - lastSyncAt`. getState() avoids a stale closure
  // so the callback reference stays stable.
  const tickClock = useCallback(() => {
    const now = Date.now();
    const last = lastTickAtRef.current;
    lastTickAtRef.current = now;
    // First tick after the clock (re)starts: anchor the timestamp, nothing to
    // debit yet.
    if (last === null) return;

    const state = useGameStore.getState();
    const turn = state.currentTurn;
    const clock = state.clockState;
    const time = turn === Color.WHITE
      ? clock.white.timeRemaining
      : clock.black.timeRemaining;
    const newTime = Math.max(0, time - (now - last));
    state.updateClock(turn, newTime);
    if (newTime <= 0) {
      state.handleTimeExpired(turn);
    }
  }, []);

  // Turn/result text
  const getTurnText = () => {
    if (isGameOver) {
      switch (status) {
        case GameStatus.WHITE_WINS: return t('game.result.whiteWins');
        case GameStatus.BLACK_WINS: return t('game.result.blackWins');
        case GameStatus.DRAW: return t('game.result.draw');
        // Senza questo caso la partita chiusa in presenza cadeva sul generico
        // «Partita terminata», che non dice la cosa che la distingue.
        case GameStatus.ABANDONED: return t('game.result.noWinner');
        default: return t('game.result.gameOver');
      }
    }
    if (aiDeclinedDraw) return t('game.turn.aiDeclinedDraw', 'Computer declined the draw');
    if (isAIThinking) return t('game.turn.aiThinking');
    if (currentTurn === localPlayerColor) return isAIMode ? t('game.turn.yourTurn') : (currentTurn === Color.WHITE ? t('game.turn.whiteToMove') : t('game.turn.blackToMove'));
    if (isAIMode) return t('game.turn.aiTurn');
    return currentTurn === Color.WHITE ? t('game.turn.whiteToMove') : t('game.turn.blackToMove');
  };

  const getResultClass = (): string => {
    if (!isGameOver) return '';
    switch (status) {
      // In presenza "vinta" e "persa" sono relative a chi sta in basso: e' l'unico
      // significato che hanno quando i due giocatori sono lo stesso dispositivo.
      case GameStatus.WHITE_WINS: return isAIMode ? (localPlayerColor === Color.WHITE ? 'win' : 'loss') : (isHotseat ? (bottomColor === Color.WHITE ? 'win' : 'loss') : '');
      case GameStatus.BLACK_WINS: return isAIMode ? (localPlayerColor === Color.BLACK ? 'win' : 'loss') : (isHotseat ? (bottomColor === Color.BLACK ? 'win' : 'loss') : '');
      case GameStatus.DRAW: return 'draw';
      default: return '';
    }
  };

  // No-op move handler (local mode uses internal store)
  const handleMove = useCallback((_move: Move) => {
    // In local mode, GameBoard reads directly from game-store
    // This is a no-op since isLocalMode=true
  }, []);

  // Review navigation lives in the moves sidebar / mobile bar (ReviewControls).
  const isReviewing = reviewIndex !== null;
  const isReturnLegPending = matchLegs === 2 && matchLegIndex === 1;

  // Orientamento. Contro il computer la damiera parte dal lato dell'umano; in
  // presenza parte sempre col Bianco in basso, e il bottone "gira" la ribalta —
  // due persone attorno a un telefono cambiano lato a meta' partita.
  const [userFlipped, setUserFlipped] = useState(false);
  const baseFlipped = isAIMode && localPlayerColor === Color.BLACK;
  const flipped = baseFlipped !== userFlipped; // XOR
  // Chi sta in basso. E' questo — non `localPlayerColor` — che GameScreen usa per
  // assegnare orologi, pezzi mangiati e schede: senza, girare la damiera muoverebbe
  // i pezzi e lascerebbe gli orologi dov'erano.
  const bottomColor = flipped ? Color.BLACK : Color.WHITE;
  const aiLabelKey = AI_MODE_LABEL_KEY[gameMode];
  const computerName = aiLabelKey
    ? `${t('game.opponent.computer')} (${t(`game.modes.${aiLabelKey}`)})`
    : t('game.opponent.computer');
  // No `profileUsername` on the top card: it's either the engine or, in
  // hot-seat, a colour label — neither is an account. Same for the bottom card
  // in hot-seat, where "White"/"Black" say nothing about who is sitting there.
  /* Memoizzati per la stessa ragione di `mobilePanels`: scendono a `GameScreen`,
     che e' memoizzato, e un oggetto nuovo a ogni render del contenitore lo
     farebbe ri-renderizzare quattro volte al secondo insieme all'orologio. */
  const topPlayer = useMemo(
    () => ({ username: isAIMode ? computerName : (bottomColor === Color.WHITE ? t('game.clock.black') : t('game.clock.white')) }),
    [isAIMode, computerName, bottomColor, t],
  );
  const bottomPlayer = useMemo(
    () => (isAIMode
      ? { username: t('common.you'), avatar: currentUser?.avatar ?? null, country: currentUser?.country ?? null, profileUsername: currentUser?.username ?? null }
      : { username: bottomColor === Color.WHITE ? t('game.clock.white') : t('game.clock.black') }),
    [isAIMode, bottomColor, t, currentUser?.avatar, currentUser?.country, currentUser?.username],
  );

  // "Gira la damiera" viaggia nel focusSlot invece di essere un quinto bottone.
  // La barra e' dichiaratamente a quattro posti — a 360px non c'e' larghezza per
  // un quinto — e questo slot esiste esattamente per prendere il posto di Focus
  // quando c'e' qualcosa di piu' urgente da offrire. Solo in presenza: contro il
  // computer girare metterebbe in basso la scheda del motore, che dice "Computer".
  const giraDamiera = useCallback(() => setUserFlipped((v) => !v), []);
  const flipSlot = useMemo(
    () => (isHotseat ? <FlipBoardButton onClick={giraDamiera} /> : undefined),
    [isHotseat, giraDamiera],
  );

  /*
   * La fetta di BoardState che serve a GameScreen: lo storico per le catture nei
   * cartellini, e i due contatori + variante per la riga «quanto manca alla
   * patta» (`halfMoveClock` per il limite di mosse, `frisianEndgamePly` per le 7
   * mosse frisoni).
   *
   * ⚠️ QUESTA FETTA E' IL FILO CHE SI ROMPE IN SILENZIO. Gli altri quattro
   * contenitori (online, torneo, arena, spettatore) passano il BoardState
   * INTERO: un campo nuovo gli arriva da solo. Qui no — va aggiunto a mano, e se
   * manca la riga non compare contro l'AI e in presenza mentre online funziona,
   * che e' il genere di buco che i test del comportamento non vedono.
   *
   * Memoizzata perche' l'orologio ridisegna GameScreen piu' volte al secondo, e
   * un oggetto nuovo a ogni giro rifarebbe ogni volta le scansioni dello storico
   * che GameScreen memoizza proprio su questo prop. Ogni campo aggiunto qui va
   * anche nell'array di dipendenze: GameScreen e' `memo()` con confronto
   * superficiale, quindi un campo che cambia senza cambiare l'identita'
   * dell'oggetto resta congelato sul primo valore.
   */
  const boardStateSlice = useMemo(
    () => ({ moveHistory, halfMoveClock, frisianEndgamePly, variant }),
    [moveHistory, halfMoveClock, frisianEndgamePly, variant]
  );

  // Mobile-only: the move list opens as a full-screen overlay (AI mode has no
  // chat). Desktop keeps the inline LocalMoveList sidebar.
  /*
   * `useMemo`, e non per eleganza: questo array scendeva NUOVO a ogni render —
   * nuovo oggetto, nuova icona, nuova arrow — e `LocalGame` si ri-renderizza a
   * ogni tick dell'orologio. Con una prop d'identita' nuova, il `memo` su
   * `GameScreen` e' peso morto: c'e' l'involucro e non ferma niente. E' la
   * stessa trappola del commit `d9aaf129` (le callback verso la scacchiera che
   * cambiavano a ogni tick), vista dall'altro capo dell'albero.
   */
  /* Stesso motivo di `topPlayer`: questi tre scendono a un componente
     memoizzato, e come JSX inline cambierebbero identita' a ogni tick. */
  const boardOverlaySlot = useMemo(() => (
    <DrawnOpeningBanner
      opening={drawnOpening}
      triggerKey={startedAtMs}
      legLabel={matchLegs === 2 ? t('online.match.legShort', { leg: matchLegIndex, total: matchLegs }) : undefined}
    />
  ), [drawnOpening, startedAtMs, matchLegs, matchLegIndex, t]);

  // A fine andata il bottone non offre una partita nuova ma IL RITORNO: la stessa
  // apertura sorteggiata, rigiocata dall'altra parte del tavolo.
  const overlaySlot = useMemo(() => (
    <GameOverModal
      onNewGame={isReturnLegPending ? startReturnLeg : resetGame}
      primaryLabel={isReturnLegPending ? t('game.match.playReturn') : undefined}
      onLeave={onLeave}
    />
  ), [isReturnLegPending, startReturnLeg, resetGame, onLeave, t]);

  const sidebarSlot = useMemo(() => (
    <>
      <ReviewControls variant="panel" openingPlyCount={openingPlyCount} />
      <LocalMoveList />
    </>
  ), [openingPlyCount]);

  const mobilePanels: MobilePanel[] = useMemo(() => [
    {
      id: 'moves',
      label: t('game.sidebar.moves', 'Moves'),
      icon: <List size={20} aria-hidden="true" />,
      render: () => <LocalMoveList />,
    },
  ], [t]);

  return (
    <>
      <GameScreen
        boardState={boardStateSlice}
        playerColor={bottomColor === Color.WHITE ? 'white' : 'black'}
        // Il valore onesto: "tocca a chi sta in basso". In presenza serve a
        // illuminare UNA scheda sola; ad aprire la damiera a entrambi i colori
        // pensa `hotSeat`.
        isMyTurn={isHotseat
          ? (currentTurn === bottomColor && !isGameOver && !isReviewing)
          : (!isAITurn && !isGameOver && !isReviewing)}
        hotSeat={isHotseat}
        /* `isMyTurn` qui sopra e' falso anche mentre si sfogliano le mosse: senza
           questo la card dell'avversario, che si accende per negazione, si
           prenderebbe il tratto. Vedi la nota su `isReviewing` in GameScreen. */
        isReviewing={isReviewing}
        isGameOver={isGameOver}
        flipped={flipped}
        onMove={handleMove}
        allowPremove={isAIMode}
        clockState={networkClockState}
        isPlaying={isClockActive}
        tickClock={tickClock}
        topPlayer={topPlayer}
        bottomPlayer={bottomPlayer}
        turnText={getTurnText()}
        resultClass={getResultClass()}
        /* In presenza non si consegna la partita a nessuno: si chiude. Contro il
           computer resta una resa vera, con la sua sconfitta. */
        onResign={isHotseat ? abandonGame : resign}
        // In presenza la patta non si propone: si chiude, con una conferma.
        onOfferDraw={isAIMode ? offerDraw : (isHotseat ? agreeDraw : undefined)}
        confirmDraw={isHotseat}
        focusSlot={flipSlot}
        drawOffered={drawOffered}
        isLocalMode={true}
        variant={getVariantString(variant) as 'international' | 'italian' | 'english'}
        timeControlLabel={timeControlLabel}
        timeControlPreset={timeControlPreset}
        isRated={false}
        ruleMode={ruleMode}
        // Stesso banner della sfida online: la scacchiera mostra già la posizione
        // post-apertura, questo spiega da dove arriva. Si ri-arma su startedAtMs,
        // che resetGame aggiorna a ogni rivincita.
        boardOverlaySlot={boardOverlaySlot}
        overlaySlot={overlaySlot}
        sidebarSlot={sidebarSlot}
        mobilePanels={mobilePanels}
        openingPlyCount={openingPlyCount}
      />
    </>
  );
}
